import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import JobListingsSection from "./JobListingsSection";
import { useTranslation } from "src/_common/hooks/useTranslation";
import UpcomingInterviewSection from "./UpcomingInterviewSection";
import ApplicationChart from "./ApplicationChart";
import PendingOfferActionsSection from "./PendingOfferActionsSection";
import { handleApiCall } from "src/_config";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";

export default function DashboardMain() {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const jobListings = useJobListSelector();
  const { callGetJobStatusCounts } = useEmployerJobApi();
  const [jobStatusCounts, setJobStatusCounts] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const userDetails = useAppUserDetailsSelector();
  const userApi = useUserApi();
  const toast = useToaster();

  const [isResendingVerification, setIsResendingVerification] = useState(false);

  // Check if email is not verified — only show banner after user data has loaded
  const isEmailNotVerified = userDetails?.user != null && userDetails?.user?.verify_email !== 1;

  const handleResendVerification = () => {
    if (!userDetails?.user?.email || isResendingVerification) return;
    setIsResendingVerification(true);
    userApi.callResendVerification(
      { email: userDetails?.user?.email },
      (message: string) => {
        setIsResendingVerification(false);
        toast.success(message || "Verification email sent successfully.");
      },
      (error: string) => {
        setIsResendingVerification(false);
        toast.error(error || "Failed to resend verification email.");
      },
    );
  };

  // Fetch user profile data on component mount
  const userMeCall = () => {
    handleApiCall(userApi.callGetMe, null, () => {}, toast, false, true);
  };

  // Load job status counts on component mount
  useEffect(() => {
    userMeCall();
    loadJobStatusCounts();
  }, []);

  const loadJobStatusCounts = (jobIds: string[] = []) => {
    setLoading(true);
    callGetJobStatusCounts(
      jobIds,
      (message: string, data: any) => {
        setJobStatusCounts(data);
        setLoading(false);
      },
      (error: string) => {
        console.error("Failed to load job status counts:", error);
        setLoading(false);
      },
    );
  };

  return (
    <div className="dashboard-main">
      {/* Email Verification Banner */}
      {isEmailNotVerified && (
        <div
          className="verification-banner"
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <i
            className="fa fa-exclamation-triangle"
            style={{
              color: "#856404",
              fontSize: "20px",
            }}
          ></i>
          <div style={{ flex: 1 }}>
            <h4
              style={{
                margin: "0 0 4px 0",
                color: "#856404",
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {t("dashboard.verification.title")}
            </h4>
            <p
              style={{
                margin: 0,
                color: "#856404",
                fontSize: "14px",
              }}
            >
              {t("dashboard.verification.message")}
            </p>
            <button
              onClick={handleResendVerification}
              disabled={isResendingVerification}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "#856404",
                fontSize: "14px",
                fontWeight: "600",
                textDecoration: "underline",
                cursor: isResendingVerification ? "not-allowed" : "pointer",
                marginTop: "6px",
                display: "inline-block",
                opacity: isResendingVerification ? 0.6 : 1,
              }}
            >
              {isResendingVerification ? "Sending..." : "Resend Verification Email"}
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <h1 className="dashboard-title">{t("dashboard.title")}{(userDetails?.company as any)?.name ? `, ${(userDetails?.company as any)?.name}` : ""}</h1>
        <p className="dashboard-subtitle">{t("dashboard.subtitle")}</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card active-listings">
          <div className="stat-header">
            <p className="stat-title">{t("dashboard.stats.activeListings.title")}</p>
            <div className="stat-icon active">★</div>
          </div>
          <div className="stat-number">
            {jobListings?.filter((job: any) => job.status === 1 || job.status === 10)?.length || 0}
          </div>
          <div className="stat-change">
            {jobStatusCounts?.current_week_applied_job_count} {t("dashboard.stats.activeListings.newThisWeek")}
          </div>
        </div>

        <div className="stat-card total-applications">
          <div className="stat-header">
            <p className="stat-title">{t("dashboard.stats.totalApplications.title")}</p>
            <div className="stat-icon applications"></div>
          </div>
          <div className="stat-number">{jobStatusCounts?.screened}</div>
          <div className="stat-change">
            {jobStatusCounts?.today_screened} {t("dashboard.stats.totalApplications.newToday")}
          </div>
        </div>

        <div
          className="stat-card interviews-scheduled"
          onClick={() => history.push("/interview-schedule")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-header">
            <p className="stat-title">{t("dashboard.stats.interviewsScheduled.title")}</p>
            <div className="stat-icon interviews"></div>
          </div>
          <div className="stat-number">{jobStatusCounts?.total_interview_schedule}</div>
          <div className="stat-change">
            {jobStatusCounts?.today_interview_schedule} {t("dashboard.stats.interviewsScheduled.scheduledToday")}
          </div>
        </div>

        <div className="stat-card hired-month">
          <div className="stat-header">
            <p className="stat-title">{t("dashboard.stats.hiredThisMonth.title")}</p>
            <div className="stat-icon hired"></div>
          </div>
          <div className="stat-number">{jobStatusCounts?.hired_this_month}</div>
          <div className="stat-change">
            {jobStatusCounts?.hired_today} {t("dashboard.stats.hiredThisMonth.target")}
          </div>
        </div>
      </div>

      {/* Application Statistics Chart */}
      <div className="chart-section">
        {/* Upcoming Interview Section */}
        <UpcomingInterviewSection />
        <div className="chart-container">
          <div className="section-header">
            <h2 className="section-title">{t("dashboard.chart.title")}</h2>
          </div>
          <ApplicationChart />
        </div>
      </div>

      {/* Pending Offer Actions Section */}
      <PendingOfferActionsSection />

      {/* Job Listings Section */}
      <JobListingsSection />
    </div>
  );
}
